import { Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import DashboardHome from '@/app/dashboard/main';
import DashboardBilling from '@/app/dashboard/org-settings/billing';
import DashboardSetting from '@/app/dashboard/org-settings/team-details';
import TeamManagementInvites from '@/app/dashboard/team-management/invites';
import AvailabilityPage from '@/app/dashboard/availability';
import EventTypesPage from '@/app/dashboard/event-types';
import EventTypeEditor from '@/app/dashboard/event-types/edit';
import BookingsPage from '@/app/dashboard/bookings';
import PatientsPage from '@/app/dashboard/patients';
import PatientEditPage from '@/app/dashboard/patients/edit';
import {
  AppointmentEncounterPage,
  AppointmentSummaryPage,
} from '@/app/booking/appointment/page';
import { getCurrentUser } from '@/client/sdk.gen';
import { UserResponse } from '@/client/types.gen';

function Dashboard() {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchUserData() {
      try {
        const userResponse = await getCurrentUser();
        if (userResponse.data) {
          setUser(userResponse.data);
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchUserData();
  }, []);

  // Protected route component that checks for admin role
  const AdminRoute = ({ children }: { children: React.ReactNode }) => {
    if (loading) {
      return <div>Loading...</div>;
    }

    if (!user || user.role_in_current_organisation !== 'ADMIN') {
      return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
  };

  return (
    <div>
      <Routes>
        <Route path="/" element={<DashboardHome />} />
        <Route
          path="/team-management/billing"
          element={
            <AdminRoute>
              <DashboardBilling />
            </AdminRoute>
          }
        />
        <Route
          path="/team-management/settings"
          element={
            <AdminRoute>
              <DashboardSetting />
            </AdminRoute>
          }
        />
        <Route
          path="/team-management/invites"
          element={
            <AdminRoute>
              <TeamManagementInvites />
            </AdminRoute>
          }
        />
        <Route path="/availability" element={<AvailabilityPage />} />
        <Route path="/event-types" element={<EventTypesPage />} />
        <Route
          path="/event-types/edit/:eventTypeId"
          element={<EventTypeEditor />}
        />
        <Route path="/appointments" element={<BookingsPage />} />
        <Route
          path="/appointments/encounter"
          element={<AppointmentEncounterPage />}
        />
        <Route
          path="/appointments/summary"
          element={<AppointmentSummaryPage />}
        />
        <Route path="/patients" element={<PatientsPage />} />
        <Route path="/patients/:patientId/edit" element={<PatientEditPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default Dashboard;
